import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import CreateButton from './CreateButton';
import PageNavigator from './PageNavigator';
import OfferDetails from './OfferDetails';
import AddOffer from './AddOffer';
import Dialog from './Dialog';
import styles from './MyClubTabContent.module.css';
import { createClubOffer, fetchClubOffers, deleteClubOffer } from '../services/clubService';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import { useEffect } from 'react';

const OFFERS_PER_PAGE = 6;

const MyClubTabContent = () => {
  const dispatch = useDispatch();

  // Club permissions
  const { hasAddClub, hasDeleteClub, permissionsLoading } = useCachedPermissions();

  const [currentPage, setCurrentPage] = useState(1);
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showAddOffer, setShowAddOffer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);

  // Add pagination state for server-side pagination
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: OFFERS_PER_PAGE,
    results: 0,
    last_page: 1
  });

  useEffect(() => {
    loadOffers(1);
  }, []);

  const loadOffers = async (page = null) => {
    setLoading(true);
    setError(null);
    try {
      const pageToUse = page || currentPage;
      
      // Create pagination parameters
      const paginationParams = {
        page: pageToUse,
        limit: OFFERS_PER_PAGE
      };

      const response = await fetchClubOffers(paginationParams);
      
      if (response.data) {
        setOffers(response.data);
        
        // Update pagination info from server response
        if (response.pagination) {
          setPagination(response.pagination);
        } else {
          // Fallback if pagination info is not in the expected format
          setPagination({
            total: response.total || response.data.length,
            page: pageToUse,
            limit: OFFERS_PER_PAGE,
            results: response.data.length,
            last_page: Math.ceil((response.total || response.data.length) / OFFERS_PER_PAGE)
          });
        }
      } else {
        setOffers([]);
        setPagination({
          total: 0,
          page: pageToUse,
          limit: OFFERS_PER_PAGE,
          results: 0,
          last_page: 1
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch offers');
      setOffers([]);
      setPagination({
        total: 0,
        page: currentPage,
        limit: OFFERS_PER_PAGE,
        results: 0,
        last_page: 1
      });
    } finally {
      setLoading(false);
    }
  };

  // Remove client-side pagination calculations since server handles it now
  const paginatedOffers = offers;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      setCurrentPage(page);
      loadOffers(page);
    }
  };

  const handleAddOffer = () => {
    if (!hasAddClub) {
      dispatch(showErrorNotification('You do not have permission to add club offers'));
      return;
    }
    setShowAddOffer(true);
  };

  const handleAddOfferSubmit = async (newOffer) => {
    try {
      await createClubOffer({
        description: newOffer.description,
        instruction: newOffer.instruction,
        from_date: newOffer.fromDate,
        to_date: newOffer.toDate,
        image: newOffer.image, // should be the File, not preview
      });
      setShowAddOffer(false);
      dispatch(showSuccessNotification('Offer created successfully!'));
      // Reload offers from server to get updated list
      loadOffers(1);
      setCurrentPage(1);
    } catch (err) {
      dispatch(showErrorNotification(err.message || 'Failed to add offer'));
    }
  };

  const handleAddOfferCancel = () => {
    setShowAddOffer(false);
  };

  const handleOfferClick = (offer) => {
    console.log('Offer clicked:', offer);
    setSelectedOffer(offer);
  };

  const handleBackToOffers = () => {
    setSelectedOffer(null);
  };

  const handleDeleteClick = (offerId) => {
    if (!hasDeleteClub) {
      dispatch(showErrorNotification('You do not have permission to delete club offers'));
      return;
    }
    setOfferToDelete(offerId);
  };

  const handleCancelDelete = () => {
    setOfferToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!offerToDelete) return;
    
    setDeleteLoading(true);
    try {
      await deleteClubOffer(offerToDelete);
      dispatch(showSuccessNotification('Offer deleted successfully!'));
      setOfferToDelete(null);
      setSelectedOffer(null);
      // Reload offers from server to get updated list
      await loadOffers(currentPage);
    } catch (err) {
      dispatch(showErrorNotification(err.message || 'Failed to delete offer'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteOffer = async (offerId) => {
    handleDeleteClick(offerId);
  };

  const handleUpdateOffer = (updatedOffer) => {
    console.log('Updating offer:', updatedOffer);
    setOffers(prev => prev.map(offer =>
      offer.id === updatedOffer.id ? updatedOffer : offer
    ));
  };


  if (selectedOffer) {
    return (
      <OfferDetails
        offer={selectedOffer}
        onBack={handleBackToOffers}
        onDelete={handleDeleteOffer}
        onSave={handleUpdateOffer}
      />
    );
  }

  if (showAddOffer) {
    return <AddOffer onSubmit={handleAddOfferSubmit} onCancel={handleAddOfferCancel} />;
  }

  return (
    <div className={styles.myClubContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.titleSection}>
          <h2 className={styles.myClubTitle}>My Club</h2>
        </div>
        {hasAddClub && (
          <CreateButton label="+ Add Offer" onClick={handleAddOffer} />
        )}
      </div>
      {loading ? (
        <div className={styles.noOffersMessage}><h3>Loading...</h3></div>
      ) : error ? (
        <div className={styles.noOffersMessage}><h3>{error}</h3></div>
      ) : offers.length > 0 ? (
        <>
          <div className={styles.offersGrid}>
            {paginatedOffers.map(offer => (
              <div key={offer.id} className={styles.offerCard} onClick={() => handleOfferClick(offer)}>
                <div className={styles.offerImage}>
                  <img src={offer.images && offer.images.length > 0 ? offer.images[0] : ''} alt={offer.instruction} />
                </div>
                <div className={styles.offerContent}>
                  <div className={styles.offerHeader}>
                    <h3 className={styles.offerTitle}>{offer.instruction}</h3>
                    <span className={styles.offerDate}>{offer.from_date} - {offer.to_date}</span>
                  </div>
                  <p className={styles.offerDescription}>{offer.description}</p>
                </div>
                {hasDeleteClub && (
                  <button
                    className={styles.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(offer.id);
                    }}
                    title="Delete Offer"
                    disabled={deleteLoading}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3,6 5,6 21,6"></polyline>
                      <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          {pagination.last_page > 1 && (
            <PageNavigator
              currentPage={currentPage}
              totalPages={pagination.last_page}
              onPageChange={handlePageChange}
            />
          )}
        </>
      ) : (
        <div className={styles.noOffersMessage}>
          <h3>No Offers</h3>
          <p>There are currently no offers to display.</p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!offerToDelete}
        onClose={handleCancelDelete}
        title="Delete Offer"
        actions={
          <>
            <button
              className={styles.cancelButton}
              onClick={handleCancelDelete}
              disabled={deleteLoading}
            >
              Cancel
            </button>
            <button
              className={styles.deleteConfirmButton}
              onClick={handleConfirmDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </button>
          </>
        }
      >
        <p>Are you sure you want to delete this offer? This action cannot be undone.</p>
      </Dialog>
    </div>
  );
};

export default MyClubTabContent; 